import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { B } from '../constants';
import { hotels as hotelsApi } from '../api';
import { useCurrency } from '../CurrencyContext';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';

// Accommodation search, mirroring FlightSearchScreen. Queries the partner-backed
// /hotels/search endpoint and renders result cards, or an affiliate deep-link
// fallback when no partner API is configured.
export default function HotelSearchScreen({ goScreen, showToast, user, onAuthRequired }) {
  const { t } = useTranslation();
  const { currency, formatPrice } = useCurrency();

  const [destination, setDestination] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults] = useState(2);
  const [partner, setPartner] = useState('booking.com');
  const [results, setResults] = useState(null);
  const [fallbackLink, setFallbackLink] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!destination.trim()) { setError(t('hotels.destination')); return; }
    if (!checkIn || !checkOut) { setError(`${t('hotels.checkIn')} / ${t('hotels.checkOut')}`); return; }
    setLoading(true);
    setError('');
    setResults(null);
    setFallbackLink(null);
    setComparison(null);
    try {
      const res = await hotelsApi.search({
        destination: destination.trim(),
        checkIn,
        checkOut,
        adults,
        currency,
        partner,
      });
      if (res.provider && res.provider.endsWith('_fallback')) {
        setFallbackLink(res.bookingLink);
        setComparison(Array.isArray(res.comparison) ? res.comparison : null);
      } else {
        setResults(res.hotels || []);
      }
    } catch (err) {
      setError(err.message || 'Hotel search failed');
    } finally {
      setLoading(false);
    }
  };

  const labelStyle = { display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
      <button
        onClick={() => goScreen('trips')}
        style={{ background: 'none', border: 'none', color: B.muted, cursor: 'pointer', marginBottom: 16, fontSize: 14 }}
      >
        ← {t('nav.myTrips')}
      </button>

      <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, marginBottom: 8, color: B.night }}>
        🏨 {t('hotels.title')}
      </h1>
      <p style={{ color: B.muted, marginBottom: 24, fontSize: 15 }}>{t('hotels.subtitle')}</p>

      <form onSubmit={handleSearch} style={{ background: 'white', borderRadius: 16, padding: 24, marginBottom: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>{t('hotels.destination')}</label>
          <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder={t('hotels.destinationPlaceholder')} style={{ background: '#fafafa' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>{t('hotels.checkIn')}</label>
            <Input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} required style={{ background: '#fafafa' }} />
          </div>
          <div>
            <label style={labelStyle}>{t('hotels.checkOut')}</label>
            <Input type="date" min={checkIn} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} required style={{ background: '#fafafa' }} />
          </div>
          <div>
            <label style={labelStyle}>{t('hotels.adults')}</label>
            <Input type="number" min={1} max={16} value={adults} onChange={(e) => setAdults(Math.max(1, Math.min(16, parseInt(e.target.value, 10) || 1)))} style={{ background: '#fafafa' }} />
          </div>
          <div>
            <label style={labelStyle}>{t('nav.hotels')}</label>
            <Select
              value={partner}
              onChange={(v) => setPartner(v)}
              options={[
                { value: 'booking.com', label: 'Booking.com' },
                { value: 'expedia', label: 'Expedia' },
              ]}
              size="md"
              variant="filled"
            />
          </div>
        </div>

        {error && <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <button type="submit" disabled={loading} style={{
          width: '100%', padding: 14, background: loading ? '#9ca3af' : B.forest, color: 'white',
          border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
        }}>
          {loading ? t('hotels.searching') : t('hotels.search')}
        </button>
      </form>

      {comparison && comparison.length > 0 ? (
        <div style={{ padding: 24, background: 'white', borderRadius: 12 }}>
          <p style={{ color: B.muted, marginBottom: 16, fontSize: 14, textAlign: 'center' }}>
            {t('hotels.comparePartners')}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {comparison.map((c) => (
              <a
                key={c.slug}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  padding: '14px 18px', background: B.night, color: 'white', borderRadius: 10,
                  fontSize: 15, fontWeight: 700, textDecoration: 'none',
                }}
              >
                <span>{c.name}</span>
                <span aria-hidden="true">{t('hotels.viewDeal')} →</span>
              </a>
            ))}
          </div>
        </div>
      ) : fallbackLink && (
        <div style={{ textAlign: 'center', padding: 24, background: 'white', borderRadius: 12 }}>
          <p style={{ color: B.muted, marginBottom: 16, fontSize: 14 }}>{t('hotels.openPartner')}</p>
          <a href={fallbackLink} target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-block', padding: '12px 24px', background: B.amber, color: B.night,
            borderRadius: 10, fontSize: 15, fontWeight: 700, textDecoration: 'none',
          }}>
            {t('hotels.viewDeal')} →
          </a>
        </div>
      )}

      {results && results.length === 0 && !fallbackLink && (
        <div style={{ textAlign: 'center', color: B.muted, padding: 40 }}>{t('hotels.noResults')}</div>
      )}

      {results && results.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {results.map((h) => (
            <div key={h.id} style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              {h.photoUrl && <img src={h.photoUrl} alt={h.name} loading="lazy" style={{ width: '100%', height: 150, objectFit: 'cover' }} />}
              <div style={{ padding: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: B.night, marginBottom: 4 }}>{h.name}</div>
                {h.address && <div style={{ fontSize: 12, color: B.muted, marginBottom: 8 }}>{h.address}</div>}
                {h.rating != null && (
                  <div style={{ fontSize: 12, color: B.forest, marginBottom: 8 }}>
                    ★ {Number(h.rating).toFixed(1)}
                    {h.reviewCount ? ` · ${h.reviewCount} ${t('hotels.reviews')}` : ''}
                  </div>
                )}
                {h.price?.total != null && (
                  <div style={{ fontWeight: 700, color: B.night }}>
                    {formatPrice(h.price.total, { fromCurrency: h.price.currency || currency })}
                    <span style={{ fontSize: 12, fontWeight: 400, color: B.muted }}> {t('hotels.perNight')}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
